import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { completeLogin, currentUser, login } from '../auth/oidc';
import { homePathForToken } from '../auth/homePath';
import { consumeReturnPath } from '../auth/returnPath';
import { useT } from '../i18n';

/**
 * OIDC redirect landing: exchanges the code (PKCE) and returns home.
 *
 * <p>Two things here exist because of the Back button. An authorization response is single-use -
 * the `state` is consumed and deleted from session storage the moment it is redeemed - but the
 * browser keeps the URL that carried it in history like any other page. Press Back and the
 * browser restores `/callback?code=…&state=…`, this component mounts again, and oidc-client-ts
 * answers the replay with "No matching state found in storage". Which is reported as a sign-in
 * failure to somebody who is, demonstrably, signed in.
 *
 * <p>So: the query is scrubbed from the history entry before the exchange starts, and a failure
 * that turns out to have a session behind it is treated as what it is - a replay, not a failure.
 * The first makes the trap unreachable; the second makes it harmless if it is reached anyway,
 * from a bookmark or a restored tab.
 */
export function Callback() {
  const navigate = useNavigate();
  const t = useT();
  const [error, setError] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) {
      return; // StrictMode double-invoke guard: the code is single-use.
    }
    ran.current = true;

    // Take the response, then take it out of history. replaceState rather than
    // a router navigate: this has to happen before the async exchange, while
    // this entry is still the current one, and it must not add an entry of its
    // own - a Back button that lands on a dead callback is the bug.
    const responseUrl = window.location.href;
    window.history.replaceState(null, '', '/callback');

    completeLogin(responseUrl)
      .then((user) => {
        // Back to the page the sign-in interrupted, when there was one; home otherwise.
        navigate(consumeReturnPath(window.sessionStorage) ?? homePathForToken(user.access_token), {
          replace: true,
        });
      })
      .catch(async (e: unknown) => {
        // A replayed callback is not a failed sign-in. If a session is already
        // in hand the exchange did happen - once - so go where the successful
        // run would have gone rather than accusing the user of failing to
        // sign in while they are signed in.
        const existing = await currentUser();
        if (existing) {
          navigate(
            consumeReturnPath(window.sessionStorage) ?? homePathForToken(existing.access_token),
            { replace: true }
          );
          return;
        }
        setError(e instanceof Error ? e.message : String(e));
      });
  }, [navigate]);

  if (error) {
    return (
      <main>
        <p role="alert">
          {t('Sign-in failed:')} {error}
        </p>
        {/*
          Starting over, not going back. The old link here was <a href="/">, which PUSHES - so
          the dead callback stayed one Back press away and the next Back press showed this same
          page again. A fresh sign-in is the only thing that can actually resolve this state.
        */}
        <button type="button" onClick={() => void login()}>
          {t('Sign in again')}
        </button>
        <button type="button" onClick={() => window.location.replace('/')}>
          {t('Back to the workbench')}
        </button>
      </main>
    );
  }
  return <main>{t('Completing sign-in…')}</main>;
}
